import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import {
  FileText, FileSpreadsheet, ShoppingCart, Package,
  Users, Target, Ship, Loader2, Filter, X,
  Calendar as CalendarIcon, ClipboardList, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { HelpTooltip } from '../components/ui/help-tooltip';
import { format, startOfWeek, startOfMonth, startOfQuarter, subMonths, startOfYear } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` } });

// ========== Period helpers ==========
const PERIOD_OPTIONS = [
  { value: 'todos', label: 'Todo periodo' },
  { value: 'semana', label: 'Esta semana' },
  { value: 'mes', label: 'Este mes' },
  { value: 'trimestre', label: 'Este trimestre' },
  { value: 'semestre', label: 'Este semestre' },
  { value: 'ano', label: 'Este ano' },
  { value: 'personalizado', label: 'Personalizado' },
];

const getDateRange = (period) => {
  const now = new Date();
  switch (period) {
    case 'semana': return { from: startOfWeek(now, { weekStartsOn: 1 }), to: now };
    case 'mes': return { from: startOfMonth(now), to: now };
    case 'trimestre': return { from: startOfQuarter(now), to: now };
    case 'semestre': return { from: subMonths(startOfMonth(now), 5), to: now };
    case 'ano': return { from: startOfYear(now), to: now };
    default: return { from: null, to: null };
  }
};

// ========== Report definitions ==========
const HELP_TEXTS = {
  pedidos: { title: 'Pedidos de Compra', description: 'Lista completa dos pedidos emitidos, com fornecedor, projeto vinculado, valor e status atual. Util para controle de gastos e acompanhamento de entregas.' },
  cotacoes: { title: 'Cotacoes', description: 'Historico de cotacoes realizadas com fornecedores, quantidade de itens cotados e status. Permite avaliar o volume de negociacao e prazos.' },
  fornecedores: { title: 'Ranking de Fornecedores', description: 'Classificacao dos fornecedores por valor total de pedidos. Ajuda a identificar concentracao e dependencia de fornecedores especificos.' },
  inventario: { title: 'Inventario Geral', description: 'Posicao atual do estoque com custo medio, valor em estoque e itens criticos. Base para reposicao e controle patrimonial.' },
  movimentacoes: { title: 'Movimentacoes de Estoque', description: 'Registro de todas as entradas e saidas de materiais, com motivo, projeto e responsavel. Essencial para rastreabilidade.' },
  portfolio: { title: 'Portfolio de Projetos', description: 'Visao consolidada de todos os projetos com budget, gastos realizados, margem estimada e progresso. Indicador de saude financeira dos projetos.' },
  clientes: { title: 'Carteira de Clientes', description: 'Base completa de clientes cadastrados com dados de contato, segmento e origem. Util para acoes de relacionamento e marketing.' },
  pipeline: { title: 'Pipeline de Vendas', description: 'Oportunidades comerciais em andamento com valor estimado, probabilidade e etapa do funil. Projecao de receita futura.' },
  propostas: { title: 'Propostas Comerciais', description: 'Lista de propostas enviadas a clientes com valores, status e vendedor responsavel. Mede a eficiencia da area comercial.' },
  status_prod: { title: 'Status de Producao', description: 'Embarcacoes em producao com progresso geral e fases concluidas. Acompanhamento operacional da fabrica.' },
};

const REPORT_AREAS = [
  {
    id: 'compras', title: 'Compras', icon: ShoppingCart, color: 'blue',
    help: { title: 'Relatorios de Compras', description: 'Dados de pedidos, cotacoes e fornecedores. Filtros de periodo e status se aplicam a pedidos e cotacoes.' },
    reports: [
      { id: 'pedidos', title: 'Pedidos de Compra', pdf: '/relatorios/compras/pedidos/pdf', excel: '/relatorios/compras/pedidos/excel', usePeriod: true, useStatus: 'status_pedido' },
      { id: 'cotacoes', title: 'Cotacoes', pdf: '/relatorios/compras/cotacoes/pdf', excel: '/relatorios/compras/cotacoes/excel', usePeriod: true, useStatus: 'status_cotacao' },
      { id: 'fornecedores', title: 'Ranking de Fornecedores', pdf: '/relatorios/compras/fornecedores/pdf' },
    ],
  },
  {
    id: 'estoque', title: 'Estoque', icon: Package, color: 'amber',
    help: { title: 'Relatorios de Estoque', description: 'Inventario de produtos e historico de movimentacoes. Filtro de categoria se aplica ao inventario.' },
    reports: [
      { id: 'inventario', title: 'Inventario Geral', pdf: '/relatorios/estoque/inventario/pdf', excel: '/relatorios/estoque/inventario/excel', useCategoria: true },
      { id: 'movimentacoes', title: 'Movimentacoes de Estoque', excel: '/relatorios/estoque/movimentacoes/excel', usePeriod: true },
    ],
  },
  {
    id: 'projetos', title: 'Projetos', icon: ClipboardList, color: 'green',
    help: { title: 'Relatorios de Projetos', description: 'Visao gerencial dos projetos com budget, gastos e margem. Filtros de status e fabrica disponiveis.' },
    reports: [
      { id: 'portfolio', title: 'Portfolio de Projetos', pdf: '/relatorios/projetos/portfolio/pdf', excel: '/relatorios/projetos/portfolio/excel', useStatusProjeto: true, useFabrica: true },
    ],
  },
  {
    id: 'crm', title: 'Comercial / CRM', icon: Users, color: 'purple',
    help: { title: 'Relatorios Comerciais', description: 'Dados de clientes, oportunidades e propostas. Filtro de periodo se aplica ao pipeline e propostas.' },
    reports: [
      { id: 'clientes', title: 'Carteira de Clientes', pdf: '/relatorios/crm/clientes/pdf', excel: '/relatorios/crm/clientes/excel' },
      { id: 'pipeline', title: 'Pipeline de Vendas', pdf: '/relatorios/crm/pipeline/pdf', usePeriod: true },
      { id: 'propostas', title: 'Propostas Comerciais', excel: '/relatorios/crm/propostas/excel', usePeriod: true, useStatus: 'status_proposta' },
    ],
  },
  {
    id: 'producao', title: 'Producao', icon: Ship, color: 'cyan',
    help: { title: 'Relatorios de Producao', description: 'Status das embarcacoes em producao com progresso e fases. Filtro de fabrica disponivel.' },
    reports: [
      { id: 'status_prod', title: 'Status de Producao', pdf: '/relatorios/producao/status/pdf', useFabrica: true },
    ],
  },
];

const COLOR_MAP = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700', header: 'bg-blue-600' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700', header: 'bg-amber-600' },
  green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', badge: 'bg-green-100 text-green-700', header: 'bg-green-600' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-700', header: 'bg-purple-600' },
  cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', icon: 'text-cyan-600', badge: 'bg-cyan-100 text-cyan-700', header: 'bg-cyan-600' },
};

const STATUS_PEDIDO = ['pendente', 'aprovado', 'enviado', 'recebido', 'parcial'];
const STATUS_COTACAO = ['aberta', 'em_andamento', 'respondida', 'finalizada'];
const STATUS_PROPOSTA = ['rascunho', 'enviada', 'aceita', 'recusada'];
const STATUS_PROJETO = ['planejamento', 'em_andamento', 'pausado', 'concluido'];

const Relatorios = () => {
  const [downloading, setDownloading] = useState(null);
  const [factories, setFactories] = useState([]);
  const [categorias, setCategorias] = useState([]);

  // Global filters
  const [period, setPeriod] = useState('todos');
  const [customRange, setCustomRange] = useState({ from: null, to: null });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [statusPedido, setStatusPedido] = useState('');
  const [statusCotacao, setStatusCotacao] = useState('');
  const [statusProposta, setStatusProposta] = useState('');
  const [statusProjeto, setStatusProjeto] = useState('');
  const [fabrica, setFabrica] = useState('');
  const [categoria, setCategoria] = useState('');

  const dateRange = React.useMemo(() => {
    if (period === 'personalizado') return customRange;
    if (period === 'todos') return { from: null, to: null };
    return getDateRange(period);
  }, [period, customRange]);

  const hasActiveFilters = period !== 'todos' || statusPedido || statusCotacao || statusProposta || statusProjeto || fabrica || categoria;

  useEffect(() => {
    const load = async () => {
      try {
        const [factRes, catRes] = await Promise.all([
          api.get('/factories').catch(() => ({ data: [] })),
          api.get('/categorias-produto').catch(() => ({ data: [] })),
        ]);
        setFactories(Array.isArray(factRes.data) ? factRes.data : []);
        setCategorias(Array.isArray(catRes.data) ? catRes.data : []);
      } catch (e) { console.error('Erro ao carregar filtros:', e); }
    };
    load();
  }, []);  // eslint-disable-line -- load is inline, API/axios are stable

  const clearFilters = () => {
    setPeriod('todos');
    setCustomRange({ from: null, to: null });
    setStatusPedido('');
    setStatusCotacao('');
    setStatusProposta('');
    setStatusProjeto('');
    setFabrica('');
    setCategoria('');
  };

  const buildQueryString = (report) => {
    const params = new URLSearchParams();
    if (report.usePeriod && dateRange.from) {
      params.set('date_from', dateRange.from.toISOString());
      if (dateRange.to) params.set('date_to', dateRange.to.toISOString());
    }
    if (report.useStatus === 'status_pedido' && statusPedido) params.set('status', statusPedido);
    if (report.useStatus === 'status_cotacao' && statusCotacao) params.set('status', statusCotacao);
    if (report.useStatus === 'status_proposta' && statusProposta) params.set('status', statusProposta);
    if (report.useStatusProjeto && statusProjeto) params.set('status', statusProjeto);
    if (report.useFabrica && fabrica) params.set('factory_id', fabrica);
    if (report.useCategoria && categoria) params.set('categoria', categoria);
    return params.toString() ? `?${params.toString()}` : '';
  };

  const getActiveFilterBadges = (report) => {
    const badges = [];
    if (report.usePeriod && period !== 'todos' && dateRange.from)
      badges.push(PERIOD_OPTIONS.find(p => p.value === period)?.label || period);
    if (report.useStatus === 'status_pedido' && statusPedido) badges.push(`Status: ${statusPedido}`);
    if (report.useStatus === 'status_cotacao' && statusCotacao) badges.push(`Status: ${statusCotacao}`);
    if (report.useStatus === 'status_proposta' && statusProposta) badges.push(`Status: ${statusProposta}`);
    if (report.useStatusProjeto && statusProjeto) badges.push(`Status: ${statusProjeto}`);
    if (report.useFabrica && fabrica) badges.push('Fabrica');
    if (report.useCategoria && categoria) badges.push(`Cat: ${categoria}`);
    return badges;
  };

  const handleDownload = async (report, type) => {
    const endpoint = type === 'pdf' ? report.pdf : report.excel;
    if (!endpoint) return;
    const key = `${report.id}-${type}`;
    setDownloading(key);
    try {
      const qs = buildQueryString(report);
      const response = await api.get(`${endpoint}${qs}`, {
        responseType: 'blob',
      });
      const ext = type === 'pdf' ? 'pdf' : 'xlsx';
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.id}_${format(new Date(), 'yyyyMMdd_HHmm')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${report.title} baixado com sucesso`);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao gerar relatorio');
    } finally {
      setDownloading(null);
    }
  };

  const totalReports = REPORT_AREAS.reduce((acc, a) => acc + a.reports.length, 0);
  const totalPdf = REPORT_AREAS.reduce((acc, a) => acc + a.reports.filter(r => r.pdf).length, 0);
  const totalExcel = REPORT_AREAS.reduce((acc, a) => acc + a.reports.filter(r => r.excel).length, 0);

  return (
    <div className="space-y-6" data-testid="relatorios-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <BarChart3 className="h-6 w-6 text-gray-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Relatorios</h1>
            <p className="text-sm text-gray-500">Exporte dados do sistema em PDF ou Excel</p>
          </div>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-gray-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-gray-900">{totalReports}</p>
            <p className="text-xs text-gray-500">Relatorios Disponiveis</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-red-600">{totalPdf}</p>
            <p className="text-xs text-gray-500">Exportacao PDF</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{totalExcel}</p>
            <p className="text-xs text-gray-500">Exportacao Excel</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{REPORT_AREAS.length}</p>
            <p className="text-xs text-gray-500">Areas Cobertas</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-purple-600">7</p>
            <p className="text-xs text-gray-500">Filtros Disponiveis</p>
          </CardContent>
        </Card>
      </div>

      {/* Global Filter Bar */}
      <Card className="border-gray-200" data-testid="filter-bar">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filtros Globais</span>
            <HelpTooltip title="Filtros Globais" description="Aplique filtros que serao usados ao gerar os relatorios. Cada relatorio indica quais filtros aceita atraves de badges coloridos no card." iconSize={14} />
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-500 ml-auto" onClick={clearFilters}>
                <X className="h-3 w-3 mr-1" /> Limpar filtros
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Period selector */}
            <Select value={period} onValueChange={(v) => { setPeriod(v); if (v !== 'personalizado') setCalendarOpen(false); }}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="filter-period">
                <CalendarIcon className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                <SelectValue placeholder="Periodo" />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>

            {period === 'personalizado' && (
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-normal" data-testid="filter-custom-date">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {customRange.from ? (
                      customRange.to
                        ? `${format(customRange.from, 'dd/MM/yy')} - ${format(customRange.to, 'dd/MM/yy')}`
                        : format(customRange.from, 'dd/MM/yyyy')
                    ) : 'Selecionar datas'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
                  <Calendar
                    mode="range"
                    selected={customRange}
                    onSelect={(range) => {
                      setCustomRange({ from: range?.from || null, to: range?.to || null });
                      if (range?.from && range?.to) setCalendarOpen(false);
                    }}
                    locale={ptBR}
                    numberOfMonths={2}
                    defaultMonth={subMonths(new Date(), 1)}
                  />
                </PopoverContent>
              </Popover>
            )}

            {period !== 'todos' && dateRange.from && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                {format(dateRange.from, 'dd/MM/yy')} - {dateRange.to ? format(dateRange.to, 'dd/MM/yy') : 'Hoje'}
              </Badge>
            )}

            <div className="w-px h-6 bg-gray-200 mx-1" />

            {/* Status Pedido */}
            <Select value={statusPedido || 'all'} onValueChange={(v) => setStatusPedido(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-status-pedido">
                <SelectValue placeholder="Status Pedido" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Status Pedido</SelectItem>
                {STATUS_PEDIDO.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Status Cotacao */}
            <Select value={statusCotacao || 'all'} onValueChange={(v) => setStatusCotacao(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-36 text-xs">
                <SelectValue placeholder="Status Cotacao" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Status Cotacao</SelectItem>
                {STATUS_COTACAO.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Status Projeto */}
            <Select value={statusProjeto || 'all'} onValueChange={(v) => setStatusProjeto(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-36 text-xs">
                <SelectValue placeholder="Status Projeto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Status Projeto</SelectItem>
                {STATUS_PROJETO.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Fabrica */}
            <Select value={fabrica || 'all'} onValueChange={(v) => setFabrica(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-36 text-xs">
                <SelectValue placeholder="Fabrica" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Fabricas</SelectItem>
                {factories.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Categoria */}
            <Select value={categoria || 'all'} onValueChange={(v) => setCategoria(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-36 text-xs">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Categorias</SelectItem>
                {categorias.map(c => <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Report Areas */}
      {REPORT_AREAS.map(area => {
        const colors = COLOR_MAP[area.color];
        const Icon = area.icon;
        return (
          <div key={area.id} data-testid={`area-${area.id}`}>
            <div className="flex items-center gap-2 mb-3">
              <Icon className={`h-5 w-5 ${colors.icon}`} />
              <h2 className="text-lg font-semibold text-gray-800">{area.title}</h2>
              <HelpTooltip title={area.help.title} description={area.help.description} iconSize={14} />
              <Badge variant="outline" className={`text-xs ${colors.badge}`}>
                {area.reports.length} {area.reports.length === 1 ? 'relatorio' : 'relatorios'}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {area.reports.map(report => {
                const help = HELP_TEXTS[report.id];
                const pdfKey = `${report.id}-pdf`;
                const excelKey = `${report.id}-excel`;
                const activeBadges = getActiveFilterBadges(report);

                return (
                  <Card key={report.id} className={`border ${colors.border} hover:shadow-md transition-shadow`} data-testid={`report-${report.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-medium text-sm text-gray-900">{report.title}</h3>
                          {help && <HelpTooltip title={help.title} description={help.description} iconSize={13} />}
                        </div>
                        <div className="flex gap-1">
                          {report.pdf && <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-red-600 border-red-200">PDF</Badge>}
                          {report.excel && <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-green-600 border-green-200">Excel</Badge>}
                        </div>
                      </div>

                      {help && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{help.description}</p>}

                      {activeBadges.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {activeBadges.map((b, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-600 border-blue-200">
                              {b}
                            </Badge>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2 mt-3">
                        {report.pdf && (
                          <Button
                            size="sm" variant="outline"
                            className="h-8 text-xs gap-1.5 flex-1 text-red-700 border-red-200 hover:bg-red-50"
                            onClick={() => handleDownload(report, 'pdf')}
                            disabled={!!downloading}
                            data-testid={`btn-pdf-${report.id}`}
                          >
                            {downloading === pdfKey ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                            Baixar PDF
                          </Button>
                        )}
                        {report.excel && (
                          <Button
                            size="sm" variant="outline"
                            className="h-8 text-xs gap-1.5 flex-1 text-green-700 border-green-200 hover:bg-green-50"
                            onClick={() => handleDownload(report, 'excel')}
                            disabled={!!downloading}
                            data-testid={`btn-excel-${report.id}`}
                          >
                            {downloading === excelKey ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                            Baixar Excel
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Relatorios;
